import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const AERIAL_BG = "/images/hero-poster.png"; // Real ARV Rotavator in the field

export default function AboutPage() {
  const { t } = useLanguage();
  const a = t.about;

  return (
    <main>
      <SEO
        title="About Us"
        path="/about"
        description="Since 2012, Shree Ram Agro Industries (ARV) has manufactured heavy-duty agricultural machinery in Punjab. Learn about our journey, values and commitment to Indian farmers."
        keywords="Shree Ram Agro Industries history, ARV company, agricultural machinery manufacturer Punjab, since 2012"
      />
      {/* Hero */}
      <section
        className="relative py-24 overflow-hidden"
        style={{
          backgroundImage: `url(${AERIAL_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(10,63,34,0.85)' }} />
        <div className="relative z-10 mx-auto max-w-3xl px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: '#FCD34D' }}>{a.badge}</span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl text-white mt-2 leading-tight">
              {a.title} <br /><span style={{ color: '#FCD34D' }}>{a.title2}</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 lg:py-20" style={{ backgroundColor: '#F9F8F4' }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="font-heading font-bold text-3xl mb-4" style={{ color: '#0A3F22' }}>Our Story</h2>
              <p className="text-base leading-relaxed mb-6" style={{ color: '#4A554A' }}>{a.description}</p>
              <div className="p-6 rounded-xl border-l-4" style={{ backgroundColor: '#EAF2EC', borderLeftColor: '#D97706' }}>
                <h3 className="font-heading font-semibold text-lg mb-2" style={{ color: '#0A3F22' }}>{a.mission_title}</h3>
                <p className="text-base italic" style={{ color: '#1B6B3D' }}>"{a.mission}"</p>
              </div>
            </motion.div>

            {/* Values Grid */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-4"
            >
              {a.values.map((v, i) => (
                <div
                  key={v.title}
                  className="p-5 rounded-xl bg-white border card-hover"
                  style={{ borderColor: '#E5E7EB' }}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                    style={{ backgroundColor: i % 2 === 0 ? '#EAF2EC' : '#FEF3C7' }}>
                    <span className="text-xl">{['🏆', '👨‍🌾', '💡', '🤝'][i]}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-sm mb-1" style={{ color: '#0A3F22' }}>{v.title}</h3>
                  <p className="text-xs leading-relaxed" style={{ color: '#4A554A' }}>{v.desc}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Manufacturing Section */}
      <section className="py-16 lg:py-20" style={{ backgroundColor: '#F9F8F4' }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading font-bold text-3xl mb-4" style={{ color: '#0A3F22' }}>{a.teamTitle}</h2>
              <p className="text-base leading-relaxed mb-6" style={{ color: '#4A554A' }}>{a.teamDesc}</p>
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { v: '40+', l: 'Skilled Workers' },
                  { v: 'Laser', l: 'Precision Cutting' },
                  { v: 'Pan India', l: 'Dealer Network' },
                ].map((s, i) => (
                  <div key={i} className="text-center p-4 rounded-xl" style={{ backgroundColor: '#EAF2EC' }}>
                    <div className="font-heading font-black text-xl" style={{ color: '#0A3F22' }}>{s.v}</div>
                    <div className="text-xs mt-1" style={{ color: '#4A554A' }}>{s.l}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden h-72 lg:h-80 relative group"
              style={{
                backgroundImage: `url(/images/farmer-workshop.jpeg)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <div className="absolute bottom-0 left-0 right-0 p-4" style={{ background: 'linear-gradient(to top, rgba(10,63,34,0.9), transparent)' }}>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#D97706', color: '#fff' }}>Our Workshop</span>
                <p className="text-white text-sm font-semibold mt-1">ARV Rotavators built in-house — Punjab</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 text-center" style={{ backgroundColor: '#0A3F22' }}>
        <h2 className="font-heading font-bold text-2xl text-white mb-4">Ready to experience our quality?</h2>
        <div className="flex gap-4 justify-center flex-wrap">
          <Link to="/products" data-testid="about-products-btn"
            className="px-6 py-3 rounded-lg font-semibold text-white transition-all hover:scale-105"
            style={{ backgroundColor: '#D97706' }}>
            Explore Products →
          </Link>
          <Link to="/contact" data-testid="about-contact-btn"
            className="px-6 py-3 rounded-lg font-semibold text-white border transition-all hover:scale-105"
            style={{ borderColor: 'rgba(255,255,255,0.4)' }}>
            Contact Us
          </Link>
        </div>
      </section>
    </main>
  );
}
